<template>
  <div>
    <title-row
      :title="page.title"
      :subtitle="page.subtitle"
      :breadcrumb-items="[
        {
          text: 'Kamerák',
          to: { name: 'CameraList' },
        },
        page.breadcrumbItem,
      ]"
    >
      <template #buttons>
        <title-row-button @click="save" :disabled="loading" >
          <v-icon left>mdi-check</v-icon>
          Mentés
        </title-row-button>
      </template>
    </title-row>

    <loading-overlay v-if="loading" />

    <v-card flat class="rounded-xl overflow-hidden">
      <v-tabs v-model="tab" align-tabs="center" color="text--primary">
        <v-tabs-slider color="primary"></v-tabs-slider>
        <v-tab key="meta"> Metaadatok </v-tab>
        <v-tab key="live"> Élő nézet </v-tab>

        <v-tabs-items v-model="tab">
          <v-tab-item key="meta" :transition="false">
            <v-row>
              <v-col cols="12" lg="6">
                <v-card flat>
                  <v-card-title>Alapadatok</v-card-title>
                  <v-card-text>
                    <v-text-field
                      label="Megnevezés"
                      :error-messages="errors.name"
                      v-model="form.name"
                      filled />

                    <connection-text-field
                      v-if="form.connection_id"
                      :connection_id.sync="form.connection_id"
                      hint="A rendszámfelismeréshez szükséges"
                      persistent-hint />

                    <v-text-field
                      label="Felhasználónév"
                      hint="A rendszámfelismeréshez szükséges"
                      persistent-hint
                      :error-messages="errors.username"
                      v-model="form.username"
                      filled />

                    <v-text-field
                      label="Jelszó"
                      type="password"
                      hint="A rendszámfelismeréshez szükséges"
                      persistent-hint
                      :error-messages="errors.password"
                      v-model="form.password"
                      filled />

                    <v-textarea
                      label="Megjegyzés"
                      :error-messages="errors.comment"
                      v-model="form.comment"
                      filled
                      :rows="3"
                      auto-grow />
                  </v-card-text>

                  <v-card flat>
                    <v-card-title>Lókáció</v-card-title>
                    <v-card-text>

                      <v-autocomplete
                        label="Épület"
                        :error-messages="errors.building_id"
                        v-model="form.building_id"
                        :items="buildings"
                        item-text="name"
                        item-value="id"
                        filled
                        :value-comparator="$config.comparators.isEqual" />

                      <v-autocomplete
                        label="Épület szint"
                        :error-messages="errors.building_level_id"
                        v-model="form.building_level_id"
                        :items="buildingLevels"
                        item-text="name"
                        item-value="id"
                        filled
                        :value-comparator="$config.comparators.isEqual"
                        :disabled="!form.building_id"
                        :persistent-hint="!form.building_id"
                        :hint="form.building_id ? '' : 'Épület kötelező'" />

                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            label="X koordináta"
                            :error-messages="errors.x_coord"
                            v-model="form.x_coord"
                            filled
                            type="number" />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            label="Y koordináta"
                            :error-messages="errors.y_coord"
                            v-model="form.y_coord"
                            filled
                            type="number" />
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-card>
              </v-col>
              <v-col cols="12" lg="6">
                <v-card flat>
                  <v-card-title>Kapcsolat</v-card-title>
                  <v-card-text>
                    <ConnectionForm
                      v-model="form.connection"
                      :id="form.connection_id"
                      :errors="errors.connection" />
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item key="live" :transition="false">
            <v-row>
              <v-col cols="12" md="6">
                <v-card class="rounded-xl">
                  <v-card-title>
                    <v-icon left>mdi-television</v-icon>
                    Élő nézet
                  </v-card-title>
                  <v-card-text>

                    <v-text-field
                      label="RTSP cím"
                      :error-messages="errors.rtsp_url"
                      v-model="form.rtsp_url"
                      filled
                      persistent-hint
                      hint="Mentés után frissül" />

                    <div id="lpr-overlay" class="relative">
                      <LiveFeedWebrtc
                        :key="form.rtsp_url"
                        :stream-url="`https://beleptetoadmin.sze.hu/mediamtx/${form.element_id}/`" />
                      <canvas ref="canvas" width="1920" height="1080"></canvas>
                    </div>
                  </v-card-text>
                  <v-card-actions class="pa-0">
                    <v-spacer/>
                    <v-btn
                      depressed
                      color="secondary"
                      class="rounded-sm mb-4"
                      title="Hozzáadás"
                      @click="isEditingRect = !isEditingRect"
                    >
                      <div v-if="!isEditingRect">
                        <v-icon left>mdi-pencil</v-icon>
                        <span>Szerkesztés</span>
                      </div>
                      <div v-else>
                        <v-icon left>mdi-pencil-off</v-icon>
                        <span>Kész</span>
                      </div>
                    </v-btn>
                    <v-spacer/>
                  </v-card-actions>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card class="rounded-xl">
                  <v-card-title>
                    <v-icon left>mdi-vector-polygon-variant</v-icon>
                    Rendszám zónák
                  </v-card-title>
                  <MultiSelectLists
                    :headers="[
                      { text: 'Szín', value: 'color' },
                      { text: 'Poligon', value: 'polygon' },
                      { text: 'Sorompó', value: 'gate_id' }
                    ]"
                    :items="barriers"
                    v-model="form.lpr_zones"
                    :blank-item-template="{ name: '', polygon: '[[0,0], [0,0.4], [0.4,0.4], [0.4, 0]]' }">
                    <template #[`item.color`]="{ item, index }">
                      <v-sheet
                        width="20"
                        height="20"
                        :color="colors[index]"
                        rounded></v-sheet>
                    </template>
                    <template #[`item.polygon`]="{ item }">
                      <v-text-field
                        v-model="item.polygon"
                        placeholder="[[0,0], [0,0.4], [0.4,0.4], [0.4, 0]]"
                        hide-details
                        filled
                        dense />
                    </template>
                    <template #[`item.gate_id`]="{ item, items }">
                      <v-autocomplete
                        :items="items"
                        v-model="item.gate_id"
                        item-text="name"
                        item-value="element_id"
                        placeholder="Válassz"
                        hide-details
                        filled
                        dense
                        clearable />
                    </template>
                  </MultiSelectLists>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </v-card>
  </div>
</template>

<script>
const colors = [
  '#ff0000',
  '#ffff00',
  '#00ff00',
  '#00ffff',
  '#0000ff',
  '#ff00ff',
];

import LiveFeedWebrtc from '@/components/StreamPlayerWebRtc.vue';
import ConnectionTextField from '@/components/ConnectionTextField.vue';
import MultiSelectLists from '@/components/MultiSelectLists.vue';
import ConnectionForm from '@/components/forms/Connection.vue';

export default {
  components: {
    LiveFeedWebrtc,
    ConnectionTextField,
    ConnectionForm,
    MultiSelectLists,
  },

  data() {
    return {
      errors: {},
      form: {
        lpr_zones: [],
      },
      tab: 0,
      showConnectionDialog: false,
      loading: false,
      selectedPoint: null,
      isEditingRect: false,
      colors,
    };
  },

  mounted() {
    this.fetchCamera();
  },

  watch: {
    tab(val) {
      if (val === 1) {
        this.waitForCanvas();
      }
    },

    isEditingRect(val) {
      this.form.lpr_zones = [...this.form.lpr_zones];
    },

    'form.lpr_zones': {
      handler(val) {
        this.drawZones();
      },
      deep: true
    }
  },

  methods: {
    waitForCanvas() {
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (this.$refs.canvas) {
            this.addEventListener();
            this.drawZones();
          } else {
            setTimeout(() => this.waitForCanvas(), 50);
          }
        });
      });
    },

    drawZones() {
      const canvas = this.$refs.canvas;
      if (!canvas) {
        return;
      }
      const context = canvas.getContext('2d');
      context.clearRect(0, 0, canvas.width, canvas.height);

      let n = 0;
      for (const zone of this.form.lpr_zones) {
        let points = [];
        try {
          points = JSON.parse(zone.polygon);
        } catch (e) {
          continue;
        }

        context.fillStyle = colors[n] + '12';
        context.strokeStyle = colors[n] + '88';
        context.lineWidth = 10;
        context.beginPath();
        context.moveTo(points[0][0] * canvas.width, points[0][1] * canvas.height);
        for (let i = 1; i < points.length; i++) {
          context.lineTo(Math.round(points[i][0] * canvas.width), Math.round(points[i][1] * canvas.height));
        }
        context.closePath();
        context.fill();
        context.stroke();

        if (this.isEditingRect) {
          for (const point of points) {
            const px = point[0] * canvas.width;
            const py = point[1] * canvas.height;

            context.beginPath();
            context.arc(px, py, 15, 0, 2 * Math.PI);
            context.fillStyle = colors[n];
            context.fill();
            context.lineWidth = 0.1;
            context.stroke();
          }
        }
        n++;
      }
    },

    async fetchCamera() {
      this.loading = true;
      const response = await this.$http.get(`cameras/details/${this.$route.params.id || 0}`);
      this.loading = false;
      this.form = response.camera;
    },

    addEventListener() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      canvas.removeEventListener("mousedown", this.onMouseDown);
      canvas.removeEventListener("mousemove", this.onMouseMove);
      canvas.removeEventListener("mouseup", this.onMouseUp);

      canvas.addEventListener("mousedown", this.onMouseDown);
      canvas.addEventListener("mousemove", this.onMouseMove);
      canvas.addEventListener("mouseup", this.onMouseUp);
    },

    onMouseDown(event) {
      if (!this.isEditingRect) {
        return;
      }
      this.selectedPoint = this.getPressedPoint(event);
    },

    onMouseMove(event) {
      if (this.selectedPoint) {
        const canvas = this.$refs.canvas;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        const x = parseFloat(((event.clientX - rect.left) * scaleX / canvas.width).toFixed(2));
        const y = parseFloat(((event.clientY - rect.top) * scaleY / canvas.height).toFixed(2));

        const zone = this.form.lpr_zones[this.selectedPoint.zoneIndex];
        const points = JSON.parse(zone.polygon);

        points[this.selectedPoint.pointIndex] = [x, y];

        zone.polygon = JSON.stringify(points);
      }
    },

    onMouseUp(event) {
      this.selectedPoint = null;
    },

    getPressedPoint(event) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const clickX = (event.clientX - rect.left) * scaleX;
      const clickY = (event.clientY - rect.top) * scaleY;

      for (let zoneIndex = 0; zoneIndex < this.form.lpr_zones.length; zoneIndex++) {
        const zone = this.form.lpr_zones[zoneIndex];
        try {
          const points = JSON.parse(zone.polygon);
          for (let pointIndex = 0; pointIndex < points.length; pointIndex++) {
            const point = points[pointIndex];
            const px = point[0] * canvas.width;
            const py = point[1] * canvas.height;
            const distance = Math.sqrt((clickX - px) ** 2 + (clickY - py) ** 2);
            if (distance < 10) {
              return {
                zoneIndex,
                pointIndex,
                point
              };
            }
          }
        } catch (e) {
          continue;
        }
      }

      return null;
    },

    async save() {
      try {
        this.errors = {};
        this.loading = true;
        const response = await this.$http.post('cameras/save/', this.form);
        if (Object.keys(response.errors).length) {
          this.errors = response.errors;
        } else {
          if (this.$route.name !== 'CameraEditor') {
            this.$router.push({
              name: 'CameraEditor',
              params: { id: response.camera.element_id },
            });
          }
          this.fetchCamera();
          this.$dialog.notify.success('Sikeres mentés!');
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    isRtspUrlValid() {
      try {
        new URL(this.form.rtsp_url);
        return true;
      } catch (e) {
        return false;
      }
    },

    page() {
      switch (this.$route.name) {
        case 'NewCamera':
          return {
            isNew: true,
            title: 'Új kamera létrehozása',
            breadcrumbItem: {
              text: 'Kamera létrehozása',
              disabled: true,
            },
          };
        case 'CameraEditor':
          return {
            isNew: false,
            title: `Kamera: ${this.form.name ?? 'Betöltés...'}`,
            breadcrumbItem: {
              text: this.form.name,
              disabled: true,
            },
          };
      }
      return {};
    },
  },

  asyncComputed: {
    controllers: {
      async get() {
        const response = await this.$http.post('controllers/list');
        return response.controllers;
      },
      default: [],
      lazy: true,
    },

    buildings: {
      async get() {
        const response = await this.$http.post('buildings/list');
        return response.buildings;
      },
      default: [],
      lazy: true,
    },

    buildingLevels: {
      async get() {
        const response = await this.$http.post('building-levels/list', {
          building_id: this.form.building_id,
        });
        return response.building_levels;
      },
      default: [],
      lazy: true,
    },

    barriers: {
      async get() {
        const response = await this.$http.post('barriers/list', { limit: 9999 });
        return response.barriers;
      },
      default: [],
    },

    gates: {
      async get() {
        const response = await this.$http.post('elements/list', {
          model_names: ['Barriers', 'Doors', 'Turnstiles'],
          sortBy: 'Elements.type_id, Elements.id',
        });
        return response.elements;
      },
      default: [],
    },
  },

  beforeDestroy() {
    const canvas = this.$refs.canvas;
    if (canvas) {
      canvas.removeEventListener("mousedown", this.onMouseDown);
      canvas.removeEventListener("mousemove", this.onMouseMove);
      canvas.removeEventListener("mouseup", this.onMouseUp);
    }
  },
};
</script>

<style lang="scss">
#lpr-overlay {
  position: relative;

  &>canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    aspect-ratio: 16/9;
    z-index: 1;
  }
}
</style>